import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { StorageService } from '../storage/storage.service';
import { CoreUtils } from '../core-utils';
import * as AM from '../models';

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  constructor(
    private http: HttpClient,
    private storage: StorageService
  ) {}

  public get(url: string, paramsObj?: any): Observable<any> {
    const httpParams = this.generateHttpParams(paramsObj);
    const getToken$ = this.getTokenApp();

    return getToken$.pipe(
      concatMap(tokenResult => {
        return this.http.get<any>(url, { params: httpParams });
      })
    );
  }

  public getText(url: string, paramsObj?: any): Observable<any> {
    const httpParams = this.generateHttpParams(paramsObj);
    const getToken$ = this.getTokenApp();

    return getToken$.pipe(
      concatMap(tokenResult => {
        return this.http.get(url, { params: httpParams, responseType: 'text' });
      })
    );
  }

  public post(url: string, body?: any, headers?: any): Observable<any> {
    const getToken$ = this.getTokenApp();

    return getToken$.pipe(
      concatMap(tokenResult => {
        return this.http.post(url, body);
      })
    );
  }

  public put(url: string, body?: any): Observable<any> {
    const getToken$ = this.getTokenApp();

    return getToken$.pipe(
      concatMap(tokenResult => {
        return this.http.put(url, body);
      })
    );
  }

  public patch(url: string, body?: any): Observable<any> {
    const getToken$ = this.getTokenApp();

    return getToken$.pipe(
      concatMap(tokenResult => {
        return this.http.patch(url, body);
      })
    );
  }

  public delete(url: string, body?: any): Observable<any> {
    const httpParams = this.generateHttpParams(body);
    const getToken$ = this.getTokenApp();

    return getToken$.pipe(
      concatMap(tokenResult => {
        return this.http.delete(url, { params: httpParams });
      })
    );
  }

  public deleteWithBody(url: string, body?: any): Observable<any> {
    const getToken$ = this.getTokenApp();

    return getToken$.pipe(
      concatMap(tokenResult => {
        return this.http.delete(url, body);
      })
    );
  }

  public async downloadBlobAsync(
    method: string,
    url: string,
    body?: any,
    params?: any,
    headers?: any,
    defaultFilename?: string
  ): Promise<{ filename: string | null; mimeType: string | null; data: Blob | null }> {
    method = method.toLowerCase();

    await this.getTokenAppAsync();

    const httpParams = this.generateHttpParams(params);
    const httpHeaders = this.generateHttpHeaders(headers);

    const pm = new Promise<{ filename: string | null; mimeType: string | null; data: Blob | null }>((resolve, reject) => {
      this.http.request(method, url, { body: body, params: httpParams, headers: httpHeaders, responseType: 'blob', observe: 'response' }).subscribe(
        async (response: HttpResponse<Blob>) => {
          const filename = CoreUtils.getHttpResponseFilename(response, defaultFilename);
          let mimeType = response.headers.get('content-type')?.toLowerCase() as string;
          let blob = response.body;

          if (blob && filename) {
            const ext = filename.split('.').pop()?.toLowerCase();

            if (ext === 'pdf') {
              mimeType = 'application/pdf';

              const ab = await blob.arrayBuffer();
              const binArray = new Uint8Array(ab);

              blob = new Blob([binArray], { type: mimeType as string });
            }
          }

          const retObj = {
            filename: filename,
            mimeType: mimeType,
            data: blob
          };

          resolve(retObj);
        },
        (error: any) => {
          let reason = error;
          const hErr = error as HttpErrorResponse;

          if (hErr?.error?.constructor?.name === 'Blob') {
            const errBlob = hErr.error as Blob;
            const fReader = new FileReader();

            fReader.onload = () => {
              const errTxt = fReader.result as string;

              if (errBlob.type === 'application/json') {
                reason = { error: JSON.parse(errTxt) };
              } else {
                reason = { error: errTxt };
              }

              reject(reason);
            };

            fReader.readAsText(errBlob);
          } else {
            if (hErr && hErr.statusText) {
              reason = { error: hErr.statusText };
            }
            reject(reason);
          }
        }
      );
    });

    var ret = await pm;
    return ret;
  }

  public async downloadArrayBufferAsync(
    method: string,
    url: string,
    body?: any,
    params?: any,
    headers?: any,
    defaultFilename?: string
  ): Promise<{ filename: string | null; mimeType: string | null; data: ArrayBuffer | null }> {
    method = method.toLowerCase();

    await this.getTokenAppAsync();

    const httpParams = this.generateHttpParams(params);
    const httpHeaders = this.generateHttpHeaders(headers);

    const pm = new Promise<{ filename: string | null; mimeType: string | null; data: ArrayBuffer | null }>((resolve, reject) => {
      this.http.request(method, url, { body: body, params: httpParams, headers: httpHeaders, responseType: 'arraybuffer', observe: 'response' }).subscribe(
        async response => {
          const filename = CoreUtils.getHttpResponseFilename(response, defaultFilename);
          let mimeType = response.headers.get('content-type')?.toLowerCase() as string;
          let blob = response.body;

          if (blob && filename) {
            const ext = filename.split('.').pop()?.toLowerCase();

            if (ext === 'pdf') {
              mimeType = 'application/pdf';
            }
          }

          const retObj = {
            filename: filename,
            mimeType: mimeType,
            data: blob
          };

          resolve(retObj);
        },
        (error: any) => {
          let errRet: AM.BWErrorRS | null = null;
          const hErr = error as HttpErrorResponse;

          if (hErr?.error?.constructor?.name === 'ArrayBuffer') {
            const contentType = hErr.headers.get('Content-Type')?.toLowerCase();
            const isJson = contentType ? contentType.startsWith('application/json') : false;

            errRet = AM.BWErrorRS.fromArrayBuffer(hErr.error as ArrayBuffer, true);
          } else if (hErr?.statusText) {
            errRet = AM.BWErrorRS.createDefault([hErr.statusText]);
          }

          reject(errRet);
        }
      );
    });

    var ret = await pm;
    return ret;
  }

  public async getTokenAppAsync(): Promise<any> {
    const token = await this.getTokenApp().toPromise();
    return token;
  }

  // ---------- Privates ----------

  private getTokenApp(): Observable<any> {
    this.ensureUrlInStore();

    const strToken: string = this.storage.getData('appToken') as string;

    let token: any = null;
    let fetchNewToken: boolean = true;

    if (strToken) {
      token = this.parseToken(strToken);
      fetchNewToken = this.isTokenExpired(token);
    }

    if (fetchNewToken) {
      const httpCall = this.http.post(`${environment.baseUrl}Auth/Token?newUserToken=auth`, {});

      const ret = httpCall.pipe(
        map((res: any) => {
          token = res.result;
          this.storage.setData('appToken', JSON.stringify(token));
          return token;
        })
      );

      return ret;
    } else {
      return of(token);
    }
  }

  private ensureUrlInStore(): void {
    // I really don't know why this is needed! Yet following ancestral footsteps ;)
    if (!this.storage.getSessionData('url')) {
      this.storage.storeDataToSession('url', window.location.href);
    }
  }

  private parseToken(strToken: any) {
    if (strToken) {
      return JSON.parse(strToken);
    }

    return null;
  }

  private isTokenExpired(token: any) {
    if (token) {
      const countDownDate = new Date(token.expiry + 'z').getTime();
      const now = new Date().getTime();
      const distance = countDownDate - now;
      if (distance < 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  private generateHttpParams(paramsObj?: any): HttpParams {
    let ret = new HttpParams();

    if (paramsObj) {
      Object.keys(paramsObj).forEach(key => {
        const val = paramsObj[key];

        if (val) {
          ret = ret.append(key, val);
        }
      });
    }

    return ret;
  }

  private generateHttpHeaders(headers?: any): HttpHeaders {
    let ret = new HttpHeaders();

    if (headers) {
      Object.keys(headers).forEach(key => {
        const val = headers[key];

        if (val) {
          ret = ret.append(key, val);
        }
      });
    }

    return ret;
  }
}

